/**
 * Tenant Onboarding Wizard
 *
 * A multi-step guided flow for creating a new tenant with:
 * 1. Tenant Details (name, slug, description)
 * 2. Vertical Template Selection
 * 3. Plan & Quotas (limits, environments, features)
 * 4. Review & Confirm
 *
 * Uses the same POST /api/v1/admin/tenants endpoint as TenantCreatePage.
 * Vertical template selection is informational — stored in settings metadata
 * for future seeding workflows.
 */

import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import {
  WizardProgress,
  TenantDetailsStep,
  validateTenantDetails,
  VerticalTemplateStep,
  PlanQuotasStep,
  ReviewConfirmStep,
} from "../../../components/onboarding";
import type { TenantDetailsData, PlanQuotasData, WizardStep } from "../../../components/onboarding";
import { useAuthStore } from "../../../store/authStore";

import type { Tenant, TenantCreateInput } from "./types";
import { DEFAULT_TENANT_FEATURES, DEFAULT_TENANT_QUOTAS } from "./types";

// ============================================================================
// Constants
// ============================================================================

const WIZARD_STEPS: readonly WizardStep[] = [
  { number: 1, label: "Details" },
  { number: 2, label: "Templates" },
  { number: 3, label: "Plan & Quotas" },
  { number: 4, label: "Review" },
] as const;

const TOTAL_STEPS = 4;

// ============================================================================
// Wizard State Types
// ============================================================================

interface WizardState {
  readonly currentStep: number;
  readonly tenantDetails: TenantDetailsData;
  readonly selectedTemplates: readonly string[];
  readonly planQuotas: PlanQuotasData;
  readonly stepErrors: Record<string, string>;
  readonly createdTenant: Tenant | null;
}

// ============================================================================
// Main Component
// ============================================================================

export function TenantOnboardingWizard(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <WizardContent />;
}

function WizardContent(): ReactElement {
  const navigate = useNavigate();

  const [state, setState] = useState<WizardState>({
    currentStep: 1,
    tenantDetails: {
      name: "",
      slug: "",
      description: "",
    },
    selectedTemplates: [],
    planQuotas: {
      quotas: { ...DEFAULT_TENANT_QUOTAS },
      allowedEnvironments: ["dev", "staging", "prod"],
      features: { ...DEFAULT_TENANT_FEATURES },
    },
    stepErrors: {},
    createdTenant: null,
  });

  // Mutation for creating the tenant
  // Mutation for creating the tenant and provisioning selected templates
  const createMutation = useMutation({
    mutationFn: async ({
      input,
      templates,
    }: {
      input: TenantCreateInput;
      templates: readonly string[];
    }) => {
      // Step 1: Create the tenant
      const response = await apiRequest<Tenant>("/api/v1/admin/tenants", {
        method: "POST",
        body: input,
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      const tenant = response.data;

      // Step 2: Provision selected vertical templates (failures do not block tenant creation)
      for (const templateKey of templates) {
        try {
          await apiRequest(`/api/v1/tenants/${tenant.id}/provision`, {
            method: "POST",
            body: {
              verticalKey: templateKey,
              templateKey,
              name: templateKey
                .split("-")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" "),
            },
          });
        } catch {
          // Provisioning failure does not block tenant creation
        }
      }

      return tenant;
    },
    onSuccess: (tenant) => {
      setState((prev) => ({ ...prev, createdTenant: tenant }));
    },
  });

  // ---- Step Data Handlers ----

  const handleTenantDetailsChange = useCallback((data: TenantDetailsData): void => {
    setState((prev) => ({
      ...prev,
      tenantDetails: data,
      stepErrors: {},
    }));
  }, []);

  const handleTemplatesChange = useCallback((templates: readonly string[]): void => {
    setState((prev) => ({ ...prev, selectedTemplates: templates }));
  }, []);

  const handlePlanQuotasChange = useCallback((data: PlanQuotasData): void => {
    setState((prev) => ({ ...prev, planQuotas: data }));
  }, []);

  // ---- Validation ----

  const validateCurrentStep = useCallback((): boolean => {
    if (state.currentStep === 1) {
      const errors = validateTenantDetails(state.tenantDetails);
      if (Object.keys(errors).length > 0) {
        setState((prev) => ({ ...prev, stepErrors: errors }));
        return false;
      }
    }
    // Steps 2, 3, and 4 have no required fields that block advancing
    return true;
  }, [state.currentStep, state.tenantDetails]);

  // ---- Navigation ----

  const handleNext = useCallback((): void => {
    if (!validateCurrentStep()) {
      return;
    }
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, TOTAL_STEPS),
      stepErrors: {},
    }));
  }, [validateCurrentStep]);

  const handleBack = useCallback((): void => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
      stepErrors: {},
    }));
  }, []);

  // ---- Submit ----

  const handleCreate = useCallback((): void => {
    const input: TenantCreateInput = {
      name: state.tenantDetails.name.trim(),
      slug: state.tenantDetails.slug.trim(),
      settings: {
        features: state.planQuotas.features,
        quotas: state.planQuotas.quotas,
        allowedEnvironments: state.planQuotas.allowedEnvironments,
      },
    };

    createMutation.mutate({ input, templates: state.selectedTemplates });
  }, [state.tenantDetails, state.planQuotas, createMutation]);

  const handleGoToDashboard = useCallback((): void => {
    if (state.createdTenant) {
      navigate(`/studio/admin/tenants/${state.createdTenant.externalId}`);
    }
  }, [state.createdTenant, navigate]);

  // ---- Success State ----

  if (state.createdTenant) {
    return (
      <div className="p-6 max-w-2xl">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <h2 className="mt-4 text-xl font-bold">Tenant Created Successfully</h2>
          <p className="mt-2 text-foreground-muted">
            <span className="font-medium text-foreground">{state.createdTenant.name}</span> has been
            created and is ready to configure.
          </p>
          {state.selectedTemplates.length > 0 && (
            <p className="mt-1 text-sm text-foreground-muted">
              Selected templates: {state.selectedTemplates.join(", ")}
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <Link to="/studio/admin/tenants" className="btn btn-secondary">
              Back to Tenants
            </Link>
            <button onClick={handleGoToDashboard} className="btn btn-primary">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Wizard Render ----

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/studio/admin/tenants"
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenants
        </Link>
        <h1 className="text-2xl font-bold">Onboard New Tenant</h1>
        <p className="mt-1 text-foreground-muted">Set up a new tenant organization step by step.</p>
      </div>

      {/* Progress */}
      <WizardProgress steps={WIZARD_STEPS} currentStep={state.currentStep} />

      {/* Step Content */}
      <div className="min-h-[300px]">
        {state.currentStep === 1 && (
          <TenantDetailsStep
            data={state.tenantDetails}
            errors={state.stepErrors}
            onChange={handleTenantDetailsChange}
          />
        )}
        {state.currentStep === 2 && (
          <VerticalTemplateStep
            selectedTemplates={state.selectedTemplates}
            onChange={handleTemplatesChange}
          />
        )}
        {state.currentStep === 3 && (
          <PlanQuotasStep data={state.planQuotas} onChange={handlePlanQuotasChange} />
        )}
        {state.currentStep === 4 && (
          <ReviewConfirmStep
            tenantDetails={state.tenantDetails}
            selectedTemplates={state.selectedTemplates}
            planQuotas={state.planQuotas}
          />
        )}
      </div>

      {/* Error from API */}
      {createMutation.isError && (
        <div className="mt-4 rounded-md border border-error bg-error/10 p-4">
          <p className="text-sm text-error">
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : "Failed to create tenant"}
          </p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
        <div>
          {state.currentStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              disabled={createMutation.isPending}
              className="btn btn-secondary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/studio/admin/tenants" className="btn btn-secondary">
            Cancel
          </Link>

          {state.currentStep < TOTAL_STEPS ? (
            <button type="button" onClick={handleNext} className="btn btn-primary">
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Tenant"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
